import { gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import {
  ConfigGroupsSelect_ConfigFragment,
  ConfigGroupsSelect_CustomerFragment,
  ConfigGroupsSelect_GroupFragment,
} from '~components/configurations/__generated__/ConfigGroupSelect.graphql';
import { ConfigGroupsSelect } from '~components/configurations/ConfigGroupSelect';
import { useAnalyticsReporter } from '~utils/analytics';
import { ensure } from '~utils/types';
import {
  ConfigInlineGroupsSelect_ConfigFragment,
  ConfigInlineGroupsSelect_CustomerFragment,
  useConfigAddGroupMutation,
  useConfigDeleteGroupMutation,
  useConfigInlineGrouppsSelect_GroupCreateMutation,
} from './__generated__/ConfigInlineGroupsSelect.graphql';

interface Props {
  customer: ConfigInlineGroupsSelect_CustomerFragment;
  config: ConfigInlineGroupsSelect_ConfigFragment;
  isDisabled?: boolean;
}

export function ConfigInlineGroupsSelect({ customer, config, isDisabled = false }: Props) {
  const analytics = useAnalyticsReporter();
  const [createGroup, configGroupMeta] = useConfigInlineGrouppsSelect_GroupCreateMutation();
  const [configAddGroup, configAddGroupMeta] = useConfigAddGroupMutation();

  const [displayDeleteGroup, configDeleteGroupMeta] = useConfigDeleteGroupMutation();
  const isLoading = useMemo(
    () => configGroupMeta.loading || configAddGroupMeta.loading || configDeleteGroupMeta.loading,
    [configGroupMeta, configAddGroupMeta, configDeleteGroupMeta],
  );
  const handleAdd = useCallback(
    async (config: ConfigGroupsSelect_ConfigFragment, group: ConfigGroupsSelect_GroupFragment) => {
      await configAddGroup({
        variables: {
          input: {
            configurationId: config.id,
            groupId: group.id,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
      analytics.track('displaySettingUpdate', { group: 'management', changeItem: 'groups' });
    },
    [analytics, configAddGroup],
  );
  const handleRemove = useCallback(
    async (config: ConfigGroupsSelect_ConfigFragment, group: ConfigGroupsSelect_GroupFragment) => {
      await displayDeleteGroup({
        variables: {
          input: {
            configurationId: config.id,
            groupId: group.id,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
      analytics.track('displaySettingUpdate', { group: 'management', changeItem: 'groups' });
    },
    [analytics, displayDeleteGroup],
  );

  const handleCreate = useCallback(
    async (customer: ConfigGroupsSelect_CustomerFragment, name: string) => {
      const { data } = await createGroup({
        variables: {
          input: {
            customerId: customer.id,
            name,
          },
        },
      });

      await configAddGroup({
        variables: {
          input: {
            configurationId: config.id,
            groupId: ensure(data?.groupCreate?.group?.id),
          },
        },
      });
      analytics.track('displaySettingUpdate', { group: 'management', changeItem: 'groups' });
    },
    [analytics, createGroup, config.id, configAddGroup],
  );

  return (
    <ConfigGroupsSelect
      customer={customer}
      config={config}
      value={config.groups}
      isLoading={isLoading}
      isDisabled={isDisabled}
      onCreate={handleCreate}
      onAdd={handleAdd}
      onRemove={handleRemove}
    />
  );
}

ConfigInlineGroupsSelect.graphql = {
  fragments: {
    ConfigInlineGroupsSelect_config: gql`
      fragment ConfigInlineGroupsSelect_config on ConfigurationModel {
        id
        ...ConfigGroupsSelect_config
        groups {
          id
          ...ConfigGroupsSelect_group
        }
      }
    `,
    ConfigInlineGroupsSelect_customer: gql`
      fragment ConfigInlineGroupsSelect_customer on Customer {
        id
        ...ConfigGroupsSelect_customer
      }
    `,
  },
  mutations: {
    ConfigCreateGroup: gql`
      mutation ConfigInlineGrouppsSelect_GroupCreate($input: GroupCreateInput!) {
        groupCreate(input: $input) {
          group {
            id
            name
          }
          customer {
            id
            groups {
              id
              name
            }
          }
        }
      }
    `,
    ConfigAddGroup: gql`
      mutation ConfigAddGroup($input: ConfigurationAddGroupInput!) {
        configurationAddGroup(input: $input) {
          id
          groups {
            id
            name
          }
        }
      }
    `,
    ConfigDeleteGroup: gql`
      mutation ConfigDeleteGroup($input: ConfigurationDeleteGroupInput!) {
        configurationDeleteGroup(input: $input) {
          id
          groups {
            id
            name
          }
        }
      }
    `,
  },
};
